import React, { useMemo } from 'react'

// ---- Frontmatter (minimalny YAML: key: "value") ----
export const parseFrontmatter = (raw) => {
  const text = String(raw || '').replace(/^﻿/, '')
  const m = text.match(/^---\s*\r?\n([\s\S]*?)\r?\n---\s*\r?\n?/)
  if (!m) return { data: {}, body: text }
  const data = {}
  m[1].split(/\r?\n/).forEach((line) => {
    const mm = line.match(/^([A-Za-z0-9_]+)\s*:\s*(.*)$/)
    if (!mm) return
    let v = mm[2].trim()
    if ((v.startsWith('"') && v.endsWith('"')) || (v.startsWith("'") && v.endsWith("'"))) v = v.slice(1, -1)
    data[mm[1]] = v
  })
  return { data, body: text.slice(m[0].length) }
}

export const slugify = (text) =>
  String(text)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/ł/g, 'l')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

export const estimateReadingTime = (body) => {
  const words = String(body).replace(/:::component[^\n]*/g, '').replace(/[#>*`-]/g, ' ').split(/\s+/).filter(Boolean).length
  return Math.max(1, Math.round(words / 200))
}

// Wiersz separatora tabeli: |---|---| (dozwolone :---, ---:, :---:).
const isTableSeparator = (row) => {
  const t = String(row).trim()
  if (!t.includes('|') || !t.includes('-')) return false
  const cells = t.replace(/^\||\|$/g, '').split('|')
  return cells.length > 0 && cells.every((c) => /^:?-{2,}:?$/.test(c.trim()))
}

const tableCells = (row) => row.trim().replace(/^\||\|$/g, '').split('|').map((c) => c.trim())

// Obrazek w osobnej linii: ![tekst alternatywny](/img/plik-800x640.webp "Podpis"). Wymiary bierzemy
// z końcówki nazwy pliku (-800x640), żeby przeglądarka zarezerwowała miejsce przed wczytaniem.
// Ten sam zapis rozumie scripts/prerender-seo.js.
const IMG_RE = /^!\[([^\]]*)\]\((\S+?)(?:\s+"([^"]*)")?\)\s*$/
const imgSize = (src) => {
  const m = String(src).match(/-(\d+)x(\d+)\.[a-z0-9]+$/i)
  return m ? { width: Number(m[1]), height: Number(m[2]) } : {}
}

// ---- Bloki ----
export const parseBlocks = (body) => {
  const lines = String(body).split(/\r?\n/)
  const blocks = []
  let i = 0
  while (i < lines.length) {
    const line = lines[i]
    if (line.trim() === '') { i++; continue }

    let m = line.match(/^:::component\s+key="([\w-]+)"\s*$/)
    if (m) { blocks.push({ type: 'component', key: m[1] }); i++; continue }

    m = line.trim().match(IMG_RE)
    if (m) { blocks.push({ type: 'img', alt: m[1], src: m[2], caption: m[3] || '' }); i++; continue }

    // Tabela: wiersz nagłówka + zaraz po nim separator. Bez tego wiersze "| a | b |"
    // wpadały do gałęzi akapitu i sklejały się w jeden ciąg z pionowymi kreskami.
    if (line.trim().startsWith('|') && i + 1 < lines.length && isTableSeparator(lines[i + 1])) {
      const header = tableCells(line)
      i += 2
      const rows = []
      while (i < lines.length && lines[i].trim().startsWith('|')) { rows.push(tableCells(lines[i])); i++ }
      blocks.push({ type: 'table', header, rows })
      continue
    }

    m = line.match(/^(#{1,6})\s+(.*)$/)
    if (m) { const level = m[1].length; const text = m[2].trim(); blocks.push({ type: 'heading', level, text, id: slugify(text) }); i++; continue }

    if (/^[-*]\s+/.test(line)) {
      const items = []
      while (i < lines.length && /^[-*]\s+/.test(lines[i])) { items.push(lines[i].replace(/^[-*]\s+/, '')); i++ }
      blocks.push({ type: 'ul', items }); continue
    }
    if (/^\d+\.\s+/.test(line)) {
      const items = []
      while (i < lines.length && /^\d+\.\s+/.test(lines[i])) { items.push(lines[i].replace(/^\d+\.\s+/, '')); i++ }
      blocks.push({ type: 'ol', items }); continue
    }

    // Akapit. Pierwszą linię konsumujemy bezwarunkowo — inaczej linia zaczynająca
    // się od "|", ale bez separatora tabeli pod spodem, zatrzymałaby pętlę nic nie
    // konsumując i parser wpadłby w nieskończoną pętlę.
    const para = [line.trim()]
    i++
    while (i < lines.length && lines[i].trim() !== '' && !/^(#{1,6}\s|[-*]\s|\d+\.\s|:::component|\||!\[)/.test(lines[i].trim())) {
      para.push(lines[i].trim()); i++
    }
    blocks.push({ type: 'p', text: para.join(' ') })
  }
  return blocks
}

export const extractHeadings = (body) =>
  parseBlocks(body).filter((b) => b.type === 'heading' && (b.level === 2 || b.level === 3))
    .map((b) => ({ id: b.id, text: b.text, level: b.level }))

// ---- Inline ----
const INLINE_RE = /(`[^`]+`)|(\[[^\]]+\]\([^)]+\))|(\*\*[^*]+\*\*)|(\*[^*]+\*)|(https?:\/\/[^\s<)]+)/g

const renderInline = (text) => {
  const out = []
  let last = 0
  let key = 0
  let m
  INLINE_RE.lastIndex = 0
  while ((m = INLINE_RE.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index))
    const tok = m[0]
    if (m[1]) out.push(<code key={key++} className="px-1 py-0.5 rounded bg-gray-100 text-[0.85em] text-gray-800">{tok.slice(1, -1)}</code>)
    else if (m[2]) {
      const mm = tok.match(/^\[([^\]]+)\]\(([^)]+)\)$/)
      // Linki wewnętrzne (/panele, /baza-wiedzy/...) zostają w tej samej karcie —
      // nowa karta ma sens tylko dla wyjścia poza serwis.
      const internal = mm[2].startsWith('/')
      out.push(
        <a
          key={key++}
          href={mm[2]}
          {...(internal ? {} : { target: '_blank', rel: 'noopener noreferrer' })}
          className="text-blue-600 hover:underline break-words"
        >{mm[1]}</a>
      )
    }
    else if (m[3]) out.push(<strong key={key++} className="font-semibold text-gray-900">{tok.slice(2, -2)}</strong>)
    else if (m[4]) out.push(<em key={key++}>{tok.slice(1, -1)}</em>)
    else if (m[5]) out.push(<a key={key++} href={tok} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline break-words">{tok}</a>)
    last = m.index + tok.length
  }
  if (last < text.length) out.push(text.slice(last))
  return out
}

// Tabela o nagłówku „Twierdzenie | Werdykt | Co pokazują dane” renderuje się jako lista faktów i mitów ze znacznikami.
// Roboty i prerender dostają ją dalej jako zwykłą tabelę z tekstem werdyktu.
const WERDYKTY = {
  prawda: { kolor: 'text-emerald-700', tlo: 'bg-emerald-600', znak: <path d="M5 12.5l4.5 4.5L19 7.5" /> },
  nieprawda: { kolor: 'text-red-700', tlo: 'bg-red-600', znak: <path d="M7 7l10 10M17 7L7 17" /> },
  'częściowo prawda': { kolor: 'text-amber-700', tlo: 'bg-amber-500', znak: <path d="M6 12h12" /> },
  'brak dowodu': { kolor: 'text-gray-600', tlo: 'bg-gray-400', znak: <path d="M9 9.5a3 3 0 1 1 4.5 2.6c-1 .6-1.5 1.2-1.5 2.4M12 18h.01" /> },
}

export const jestTabelaFaktow = (header) => header.length === 3 && header[0] === 'Twierdzenie' && header[1] === 'Werdykt'

export const werdykt = (tekst) => WERDYKTY[String(tekst).trim().toLowerCase()] || WERDYKTY['brak dowodu']

const FaktyIMity = ({ rows }) => (
  <ul className="my-6 border-t border-gray-200">
    {rows.map((row, r) => {
      const w = werdykt(row[1])
      return (
        <li key={r} className="flex gap-3 py-4 border-b border-gray-200">
          <span className={`mt-0.5 flex-none w-6 h-6 rounded-full flex items-center justify-center ${w.tlo}`} aria-hidden="true">
            <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">{w.znak}</svg>
          </span>
          <div className="min-w-0">
            <p className="text-[15px] leading-6 font-semibold text-gray-900">{renderInline(row[0] || '')}</p>
            <p className={`text-[13px] leading-5 font-semibold mt-0.5 ${w.kolor}`}>{row[1]}</p>
            <p className="text-[14px] leading-6 text-gray-700 mt-1">{renderInline(row[2] || '')}</p>
          </div>
        </li>
      )
    })}
  </ul>
)

// components: map key -> React node
export const MarkdownBody = ({ body, components = {} }) => {
  const blocks = useMemo(() => parseBlocks(body), [body])
  return (
    <>
      {blocks.map((b, idx) => {
        switch (b.type) {
          case 'heading': {
            if (b.level === 2) return <h2 key={idx} id={b.id} className="text-xl sm:text-2xl font-bold text-gray-900 mt-10 mb-4 scroll-mt-24">{renderInline(b.text)}</h2>
            if (b.level === 3) return <h3 key={idx} id={b.id} className="text-lg font-semibold text-gray-900 mt-8 mb-3 scroll-mt-24">{renderInline(b.text)}</h3>
            return <p key={idx} className="font-semibold text-gray-900 mt-6 mb-2">{renderInline(b.text)}</p>
          }
          case 'p': return <p key={idx} className="text-[15px] leading-7 text-gray-700 mb-4">{renderInline(b.text)}</p>
          case 'ul': return <ul key={idx} className="list-disc pl-5 space-y-1.5 mb-5 text-[15px] leading-7 text-gray-700 marker:text-blue-400">{b.items.map((it, j) => <li key={j}>{renderInline(it)}</li>)}</ul>
          case 'ol': return <ol key={idx} className="list-decimal pl-5 space-y-1.5 mb-5 text-[15px] leading-7 text-gray-700 marker:text-blue-400 marker:font-medium">{b.items.map((it, j) => <li key={j}>{renderInline(it)}</li>)}</ol>
          case 'table': return jestTabelaFaktow(b.header) ? <FaktyIMity key={idx} rows={b.rows} /> : (
            // Przewijanie w poziomie w SWOIM kontenerze — szeroka tabela nie może
            // rozpychać całej strony na wąskim ekranie.
            <div key={idx} className="my-6 -mx-4 sm:mx-0 overflow-x-auto">
              <table className="w-full min-w-[520px] sm:min-w-0 border-collapse text-[14px] leading-6 sm:rounded-lg sm:border sm:border-gray-200 overflow-hidden">
                <thead>
                  <tr className="bg-gray-50">
                    {b.header.map((c, j) => (
                      <th key={j} className="text-left font-semibold text-gray-900 px-3 py-2.5 border-b border-gray-200 align-bottom">
                        {renderInline(c)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {b.rows.map((row, r) => (
                    <tr key={r} className="even:bg-gray-50/50">
                      {b.header.map((_, j) => (
                        <td key={j} className="px-3 py-2.5 border-b border-gray-100 text-gray-700 align-top">
                          {renderInline(row[j] || '')}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
          case 'img': return (
            // Zrzut otwiera się w pełnym rozmiarze w nowej karcie: na telefonie drobny tekst
            // formularza bywa nieczytelny w szerokości kolumny.
            <figure key={idx} className="my-6">
              <a href={b.src} target="_blank" rel="noopener noreferrer" className="block">
                <img
                  src={b.src}
                  alt={b.alt}
                  {...imgSize(b.src)}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-auto rounded-lg border border-gray-200 bg-white"
                />
              </a>
              {b.caption && <figcaption className="mt-2 text-[13px] leading-5 text-gray-500">{renderInline(b.caption)}</figcaption>}
            </figure>
          )
          case 'component': { const C = components[b.key]; return C ? <div key={idx} className="my-8">{C}</div> : null }
          default: return null
        }
      })}
    </>
  )
}
